import type { DisplayedPlanePlacement } from '~/engine/view/planeViewGeometry';
import type { UncommittedPointCoordinates } from '~/state/atoms/structureSet/roiAtoms';
import type { PlaneView } from '~/engine/view/views/PlaneView';
import { OFF_PLANE_OPACITY } from './AnnotationOverlaySvg';
import {
    POINT_MARKER_CROSS_ARM_PX,
    POINT_MARKER_LABEL_FONT_SIZE_PX,
    POINT_MARKER_LABEL_OFFSET_X_PX,
    POINT_MARKER_LABEL_OFFSET_Y_PX,
    POINT_MARKER_OFF_SLICE_DASH,
    POINT_MARKER_RADIUS_PX,
    POINT_MARKER_STROKE_WIDTH_PX,
} from './pointMarkerGeometry';
import {
    PoiColorPreview,
    PoiSource,
    projectPointRoisOntoPlane,
} from './tilePoiProjection';
/**
 * Marker opacity by placement: full on the plane, dimmed elsewhere in its own
 * slice, faintest (and dashed) for the selected marker on another slice.
 */
const MARKER_OPACITY: Record<DisplayedPlanePlacement, number> = {
    onPlane: 1,
    inSlice: 0.6,
    offSlice: OFF_PLANE_OPACITY,
};

interface TilePoiOverlayProps {
    view: PlaneView;
    structures: ReadonlyArray<PoiSource>;
    /**
     * The selected ROI of `structures`' structure set, drawn dimmed and
     * dashed when its point lies off the slice; undefined when the
     * selection belongs to another structure set.
     */
    selectedRoiNumber?: number;
    /**
     * The unsaved colour of an open colour edit of one of `structures`;
     * undefined when the edit belongs to another structure set.
     */
    colorPreview?: PoiColorPreview;
    /**
     * Where a move in progress of one of `structures` would place its point;
     * undefined when no move is in progress or it belongs to another
     * structure set.
     */
    pointPreview?: UncommittedPointCoordinates;
}

/**
 * Display-only SVG layer drawing every visible point ROI that lies in the
 * displayed slice, solid on the plane and dimmed elsewhere in the slice,
 * and the selected point ROI on any other slice, faint and dashed. Projection is recomputed on every
 * render, so the host re-renders it whenever the projection may change.
 */
export default function TilePoiOverlay({
    view,
    structures,
    selectedRoiNumber,
    colorPreview,
    pointPreview,
}: TilePoiOverlayProps) {
    const markers = projectPointRoisOntoPlane(
        view,
        structures,
        selectedRoiNumber,
        colorPreview,
        pointPreview
    );
    return (
        <svg
            data-testid="tile-poi-overlay"
            aria-hidden={false}
            style={{
                position: 'absolute',
                top: 0,
                left: 0,
                width: '100%',
                height: '100%',
                pointerEvents: 'none',
                zIndex: 7,
            }}
        >
            {markers.map((marker) => (
                <g
                    key={marker.roiNumber}
                    role="img"
                    aria-label={`Point of interest ${marker.name}`}
                    opacity={MARKER_OPACITY[marker.placement]}
                >
                    <circle
                        cx={marker.canvasX}
                        cy={marker.canvasY}
                        r={POINT_MARKER_RADIUS_PX}
                        fill={marker.fillCss}
                        stroke={marker.colorCss}
                        strokeWidth={POINT_MARKER_STROKE_WIDTH_PX}
                        strokeDasharray={
                            marker.placement === 'offSlice'
                                ? POINT_MARKER_OFF_SLICE_DASH
                                : undefined
                        }
                    />
                    <line
                        x1={marker.canvasX - POINT_MARKER_CROSS_ARM_PX}
                        y1={marker.canvasY}
                        x2={marker.canvasX + POINT_MARKER_CROSS_ARM_PX}
                        y2={marker.canvasY}
                        stroke={marker.colorCss}
                        strokeWidth={POINT_MARKER_STROKE_WIDTH_PX}
                    />
                    <line
                        x1={marker.canvasX}
                        y1={marker.canvasY - POINT_MARKER_CROSS_ARM_PX}
                        x2={marker.canvasX}
                        y2={marker.canvasY + POINT_MARKER_CROSS_ARM_PX}
                        stroke={marker.colorCss}
                        strokeWidth={POINT_MARKER_STROKE_WIDTH_PX}
                    />
                    <text
                        x={marker.canvasX + POINT_MARKER_LABEL_OFFSET_X_PX}
                        y={marker.canvasY + POINT_MARKER_LABEL_OFFSET_Y_PX}
                        fill={marker.colorCss}
                        fontSize={POINT_MARKER_LABEL_FONT_SIZE_PX}
                        fontFamily="sans-serif"
                        style={{
                            textShadow: '1px 1px 4px black',
                            userSelect: 'none',
                        }}
                    >
                        {marker.name}
                    </text>
                </g>
            ))}
        </svg>
    );
}
